import { FC, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  InfiniteScrollMultiSelect,
  RadixTable,
} from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentByUsedCouponData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { toast } from 'react-toastify';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload';
import {
  exportPaymentByUsedCoupon,
  getPaymentByUsedCoupon,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';
import { useCouponInfiniteOptions } from '@src/hooks/useCouponInfiniteOptions';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsByUsedCoupon: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportPaymentByUsedCouponData[]>([]);
  const [selectedCouponBatch, setSelectedCouponBatch] = useState<string>('');

  const { couponOptions, loading, hasMore, loadMore } =
    useCouponInfiniteOptions();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, tableData, displayItems } = useReportTableColumns({
    data,
    selectedItems: ['total_payment'],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item) => item.total_payment || 0,
    getPreviousPeriodField: (item) => item.total_payment_previous || 0,
    getPreviousYearField: (item) => item.total_previous_year || 0,
    columnTitleFormatter: (key) => {
      if (key === 'total_payment') return 'Total Payment';

      return key;
    },
    valueFormatter: (_, value) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item) => item.total_payment || 0,
    getPreviousPeriodField: (item) => item.total_payment_previous || 0,
    getPreviousYearField: (item) => item.total_previous_year || 0,
  });

  const buildPayload = () => {
    const commonPayload = buildCommonPayloadWithGrouping(filters);
    return {
      ...commonPayload,
      coupon_batch: Number(selectedCouponBatch),
    };
  };

  const handleRunReport = async () => {
    if (!selectedCouponBatch) {
      toast.error('Please select a coupon batch.');
      return;
    }
    filters.setIsLoading(true);
    try {
      const response = await getPaymentByUsedCoupon(buildPayload());

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const exportReport = async () => {
    if (!selectedCouponBatch) {
      toast.error('Please select a coupon batch.');
      return;
    }
    try {
      const response = await exportPaymentByUsedCoupon(buildPayload());

      downloadFile(response.data, 'payments-by-used-coupon');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payment By Used Coupon
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        >
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="coupon-select"
            >
              Coupons Batch <br />
              <span className="text-gray-400">Required</span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <InfiniteScrollMultiSelect
                hasMore={hasMore}
                id="coupon-select"
                isLoading={loading}
                isMulti={false}
                options={couponOptions}
                placeholder="-- Please Select --"
                value={selectedCouponBatch}
                onChange={(value) =>
                  setSelectedCouponBatch(value?.toString() || '')
                }
                onMenuScrollToBottom={loadMore}
              />
            </div>
          </div>
        </ReportFilteringSettings>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              <Line
                activeDot={{ r: 6 }}
                dataKey="total_payment"
                dot={{ fill: getChartColor(0), r: 4 }}
                name="Total Payment"
                stroke={getChartColor(0)}
                strokeWidth={2}
                type="monotone"
              />

              {filters.compareToPreviousPeriod && (
                <Line
                  activeDot={{ r: 4 }}
                  dataKey="total_payment_previous_period"
                  dot={{ fill: getChartColor(1), r: 3 }}
                  name="Previous Period"
                  stroke={getChartColor(1)}
                  strokeDasharray="5 5"
                  strokeWidth={1.5}
                  type="monotone"
                />
              )}

              {filters.compareToSamePeriodPreviousYear && (
                <Line
                  activeDot={{ r: 4 }}
                  dataKey="total_payment_previous_year"
                  dot={{ fill: getChartColor(2), r: 3 }}
                  name="Previous Year"
                  stroke={getChartColor(2)}
                  strokeDasharray="2 2"
                  strokeWidth={1.5}
                  type="monotone"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={exportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
